import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import DescriptionIcon from '@material-ui/icons/Description';
import GavelIcon from '@material-ui/icons/Gavel';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {
  Grid,
  Typography,
  Container,
  Paper,
  IconButton
} from '@material-ui/core';
import { Card, CardActions, CardContent, CardMedia } from '@mui/material';

const styles = (theme) => ({
  section: {
    padding: '60px 0',
    width: '100%',
    boxSizing: 'border-box',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '60px',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  sectionNumber: {
    fontSize: '14px',
    letterSpacing: '3px',
    color: '#d4145a',
    fontFamily: '"Segoe UI", sans-serif',
    fontWeight: 600,
    marginBottom: '15px',
  },
  sectionTitleText: {
    fontSize: '48px',
    marginTop: '15px',
    marginBottom: '20px',
    fontWeight: 700,
    color: '#1a1a1a',
    fontFamily: '"Georgia", serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '32px',
    },
  },
  sectionDescription: {
    fontSize: '18px',
    color: '#555',
    maxWidth: '700px',
    margin: '0 auto',
    fontFamily: '"Segoe UI", sans-serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '30px',
    marginBottom: '60px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  featureBox: {
    background: 'white',
    border: '2px solid #1e3a5f',
    padding: '30px',
    transition: 'all 0.3s',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      background: '#1e3a5f',
      color: 'white',
    },
    '&:hover $featureIcon': {
      color: 'white',
    },
    '&:hover $featureTitle': {
      color: 'white',
    },
    '&:hover $featureDescription': {
      color: '#e0e0e0',
    },
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '25px',
    color: '#d4145a',
    transition: 'color 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureTitle: {
    fontSize: '24px',
    marginBottom: '15px',
    color: '#1a1a1a',
    transition: 'color 0.3s',
    fontWeight: 600,
    fontFamily: '"Georgia", serif',
  },
  featureDescription: {
    fontSize: '15px',
    lineHeight: 1.7,
    fontFamily: '"Segoe UI", sans-serif',
    color: '#666',
    transition: 'color 0.3s',
  },
  cardActions: {
    display: 'none',
  },
  caseTypesList: {
    margin: '15px 0 0 0',
    padding: 0,
    listStyle: 'none',
  },
  caseTypeItem: {
    padding: '8px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  caseTypeText: {
    fontSize: '14px',
    color: '#e0e0e0',
    fontFamily: '"Segoe UI", sans-serif',
  },
  expandIcon: {
    marginLeft: '5px',
    fontSize: '20px',
    transition: 'transform 0.3s',
    verticalAlign: 'middle',
  },
  expandIconExpanded: {
    transform: 'rotate(180deg)',
  }
});

function HomeCards(props) {
  const { classes } = props;
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  // Simplified cards without image references that don't exist in assets folder
  const cards = [{
    "title": "Family Law Services",
    "description": "We provide comprehensive assistance for family matters including divorce proceedings, child custody arrangements, maintenance claims, domestic violence protection, and succession planning.",
    "path": "family-law"
  }, {
    "title": "High Court Practice",
    "description": "We represent clients in the Allahabad High Court, Delhi High Court, Mumbai High Court, and other judicial forums across North India, handling civil appeals, criminal appeals, writ petitions, and constitutional matters.",
    "path": "high-court-practice"
  }, {
    "title": "Litigation and Case Support",
    "description": "We represent clients in court proceedings and offer dispute resolution services with a strategic approach to achieve favorable outcomes in your legal matters.",
    "path": "litigation-investigation-support",
    "caseTypes": [
      { "name": "Criminal Defense", "path": "/criminal-case" },
      { "name": "High Court Representation", "path": "/high-court" },
      { "name": "Constitutional Matters", "path": "/constitutional-matters" },
      { "name": "Consumer Disputes", "path": "/consumer-disputes" },
      { "name": "Property Matters", "path": "/property-matters" },
      { "name": "Motor Accident Claims", "path": "/accident-claims" },
      { "name": "Civil Disputes", "path": "/civil-disputes" },
      { "name": "Debt Recovery", "path": "/debt-recovery" },
      { "name": "Corporate Litigation", "path": "/corporate-litigation" },
      { "name": "Income Tax Appeals", "path": "/tax-appeals" }
    ]
  }, {
    "title": "Documentation Services",
    "description": "We prepare, review, and register legal documents including property agreements, wills, partnership deeds, affidavits, and notices while ensuring compliance with relevant laws and regulations.",
    "path": "documentation-services"
  }, {
    "title": "Corporate Legal Advisory",
    "description": "We provide comprehensive legal counsel to businesses on regulatory compliance, corporate governance, contract drafting and review, dispute resolution, and general business legal matters.",
    "path": "corporate-legal"
  }]
  
  return (
    <section className={classes.section} id="services">
      <div className={classes.sectionHeader}>
        <div className={classes.sectionNumber}>II. OUR SERVICES</div>
        <Typography variant="h2" className={classes.sectionTitleText}>
          Legal Services
        </Typography>
        <Typography className={classes.sectionDescription}>
          We offer a wide range of legal services to help individuals and businesses navigate complex legal challenges efficiently
        </Typography>
      </div>
        
      <div className={classes.featureGrid}>
        {cards.map((card, index) => (
          <div 
            className={classes.featureBox} 
            key={index} 
            onClick={() => {
              if (index === 2) { // Only for Litigation and Investigation Support
                setExpandedIndex(expandedIndex === 2 ? null : 2);
              } else {
                navigate(card.path);
              }
            }}
          >
            <div className={classes.featureIcon}>
              {index === 0 ? <DescriptionIcon fontSize="large" /> : 
               index === 1 ? <GavelIcon fontSize="large" /> : 
               index === 2 ? <GavelIcon fontSize="large" /> : 
               index === 3 ? <DescriptionIcon fontSize="large" /> : 
               <SearchIcon fontSize="large" />}
            </div>
            <Typography variant="h3" className={classes.featureTitle}>
              {card.title}
              {index === 2 && <ExpandMoreIcon 
                className={`${classes.expandIcon} ${expandedIndex === 2 ? classes.expandIconExpanded : ''}`} 
              />}
            </Typography>
            <Typography className={classes.featureDescription}>
              {card.description}
            </Typography>
            
            {index === 2 && (
              <Collapse in={expandedIndex === 1} timeout="auto" unmountOnExit>
                <List className={classes.caseTypesList}>
                  {card.caseTypes.map((caseType, i) => (
                    <ListItem 
                      key={i} 
                      className={classes.caseTypeItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(caseType.path);
                      }}
                    >
                      <ListItemText 
                        primary={caseType.name} 
                        className={classes.caseTypeText}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default withStyles(styles)(HomeCards);
